const STORAGE_KEY = 'zooplicity_dual_cart_saved_v1';

const rootPath = () => {
  const root = window.Shopify?.routes?.root || '/';
  return root.endsWith('/') ? root : `${root}/`;
};

const fetchCart = async () => {
  const response = await fetch(`${rootPath()}cart.js`, {
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) {
    throw new Error('Nie udało się odczytać koszyka.');
  }

  return response.json();
};

const normalizeProperties = (properties) => {
  if (!properties || typeof properties !== 'object') return undefined;
  return properties;
};

const serializeLine = (item) => {
  const sellingPlan = item.selling_plan_allocation?.selling_plan?.id || null;

  return {
    id: item.variant_id || item.id,
    quantity: item.quantity,
    properties: normalizeProperties(item.properties),
    selling_plan: sellingPlan,
  };
};

const setStatus = (message) => {
  document.querySelectorAll('[data-zooplicity-dual-cart-status]').forEach((node) => {
    node.hidden = !message;
    node.textContent = message || '';
  });
};

const setActionsDisabled = (disabled) => {
  document.querySelectorAll('[data-zooplicity-checkout-mode]').forEach((button) => {
    button.disabled = disabled;
    if (disabled) {
      button.setAttribute('aria-disabled', 'true');
    } else {
      button.removeAttribute('aria-disabled');
    }
  });
};

const removeLinesDescending = async (lines) => {
  const ordered = [...lines].sort((a, b) => b.line - a.line);

  for (const item of ordered) {
    const response = await fetch(`${rootPath()}cart/change.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        line: item.line,
        quantity: 0,
      }),
    });

    if (!response.ok) {
      throw new Error('Nie udało się przygotować wybranej części koszyka.');
    }
  }
};

const startPartialCheckout = async (mode) => {
  setActionsDisabled(true);
  setStatus('Przygotowuję wybraną część koszyka…');

  try {
    const cart = await fetchCart();

    const indexed = cart.items.map((item, index) => ({
      line: index + 1,
      item,
      subscription: Boolean(item.selling_plan_allocation),
    }));

    const excluded =
      mode === 'one-time'
        ? indexed.filter((entry) => entry.subscription)
        : indexed.filter((entry) => !entry.subscription);

    const selected =
      mode === 'one-time'
        ? indexed.filter((entry) => !entry.subscription)
        : indexed.filter((entry) => entry.subscription);

    if (!selected.length) {
      throw new Error('Wybrana część koszyka jest pusta.');
    }

    const savedPayload = {
      createdAt: Date.now(),
      mode,
      items: excluded.map((entry) => serializeLine(entry.item)),
    };

    await removeLinesDescending(excluded);
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(savedPayload));
    window.location.assign(`${rootPath()}checkout`);
  } catch (error) {
    setActionsDisabled(false);
    setStatus(error instanceof Error ? error.message : 'Nie udało się przygotować checkoutu.');
  }
};

const startFullCheckout = async () => {
  setActionsDisabled(true);
  setStatus('Sprawdzam cały koszyk…');

  try {
    const cart = await fetchCart();
    const hasOneTime = cart.items.some((item) => !item.selling_plan_allocation);
    const hasSubscription = cart.items.some((item) => Boolean(item.selling_plan_allocation));

    if (!hasOneTime || !hasSubscription) {
      throw new Error(
        'Koszyk nie zawiera teraz obu części. Odśwież stronę i spróbuj ponownie.',
      );
    }

    window.localStorage.removeItem(STORAGE_KEY);
    window.location.assign(`${rootPath()}checkout`);
  } catch (error) {
    setActionsDisabled(false);
    setStatus(
      error instanceof Error
        ? error.message
        : 'Nie udało się otworzyć checkoutu z całym koszykiem.',
    );
  }
};

const restoreSavedItems = async () => {
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (!raw) return;

  let payload;
  try {
    payload = JSON.parse(raw);
  } catch {
    window.localStorage.removeItem(STORAGE_KEY);
    return;
  }

  if (!payload?.items?.length) {
    window.localStorage.removeItem(STORAGE_KEY);
    return;
  }

  setStatus('Przywracam odłożoną część koszyka…');

  const items = payload.items.map((item) => {
    const result = {
      id: item.id,
      quantity: item.quantity,
    };

    if (item.properties && Object.keys(item.properties).length) {
      result.properties = item.properties;
    }

    if (item.selling_plan) {
      result.selling_plan = item.selling_plan;
    }

    return result;
  });

  try {
    const response = await fetch(`${rootPath()}cart/add.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ items }),
    });

    if (!response.ok) {
      throw new Error('Nie udało się przywrócić odłożonej części koszyka.');
    }

    window.localStorage.removeItem(STORAGE_KEY);
    window.location.reload();
  } catch (error) {
    setStatus(error instanceof Error ? error.message : 'Nie udało się przywrócić koszyka.');
  }
};

let groupingScheduled = false;

const groupCartRows = () => {
  groupingScheduled = false;

  document.querySelectorAll('.cart-items__table tbody').forEach((tbody) => {
    const rows = [...tbody.querySelectorAll('tr[data-zooplicity-purchase-type]')];
    if (!rows.length) return;

    const oneTime = rows.filter(
      (row) => row.dataset.zooplicityPurchaseType === 'one-time',
    );

    const subscriptionGroups = new Map();

    rows
      .filter((row) => row.dataset.zooplicityPurchaseType === 'subscription')
      .forEach((row) => {
        const planName = (row.dataset.zooplicityPlanName || '').trim() || 'Regularna paczka';

        if (!subscriptionGroups.has(planName)) {
          subscriptionGroups.set(planName, []);
        }

        subscriptionGroups.get(planName).push(row);
      });

    const subscription = [...subscriptionGroups.values()].flat();
    const desired = [...oneTime, ...subscription];

    const current = rows.map((row) => row.dataset.key || '').join('|');
    const target = desired.map((row) => row.dataset.key || '').join('|');

    if (current !== target) {
      desired.forEach((row) => tbody.append(row));
    }

    rows.forEach((row) => {
      row.classList.remove('zooplicity-cart-group-first');
      row.removeAttribute('data-zooplicity-group-label');
    });

    if (oneTime[0]) {
      oneTime[0].classList.add('zooplicity-cart-group-first');
      oneTime[0].setAttribute('data-zooplicity-group-label', tbody.closest('.cart-page')?.dataset.zpCartOneLabel || 'Zakupy jednorazowe');
    }

    subscriptionGroups.forEach((groupRows, planName) => {
      if (!groupRows[0]) return;

      groupRows[0].classList.add('zooplicity-cart-group-first');
      groupRows[0].setAttribute(
        'data-zooplicity-group-label',
        `${tbody.closest('.cart-page')?.dataset.zpCartRegularLabel || 'Regularna paczka'} · ${planName}`,
      );
    });
  });
};

const scheduleGrouping = () => {
  if (groupingScheduled) return;
  groupingScheduled = true;
  window.requestAnimationFrame(groupCartRows);
};

document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-zooplicity-checkout-mode]');
  if (!button) return;

  const mode = button.dataset.zooplicityCheckoutMode;

  if (mode === 'all') {
    event.preventDefault();
    startFullCheckout();
    return;
  }

  if (mode !== 'one-time' && mode !== 'subscription') return;

  event.preventDefault();
  startPartialCheckout(mode);
});

const observer = new MutationObserver(scheduleGrouping);
observer.observe(document.documentElement, {
  childList: true,
  subtree: true,
});

scheduleGrouping();
restoreSavedItems();
